/**
 * Normalizes a phone string for use in tel: links (E.164-like format).
 *
 * Handles Swiss local numbers (leading 0 → +41), international prefixes (+ or 00),
 * and strips formatting characters. Falls back to raw cleaned value if
 * the number cannot be confidently normalized.
 */
const normalizePhoneForDialSingle = (phone = "") => {
  if (!phone || typeof phone !== "string") return "";

  let cleaned = phone.replace(/[\s.\-\(\)]/g, "");

  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  if (cleaned.startsWith("00")) {
    return "+" + cleaned.slice(2);
  }

  // Handle Swiss country code entered without plus (e.g. 41781234567).
  if (cleaned.startsWith("41")) {
    return "+" + cleaned;
  }

  if (cleaned.startsWith("0")) {
    return "+41" + cleaned.slice(1);
  }

  // Legacy case: local Swiss number entered without leading 0 (e.g. 276034200).
  // Add the missing zero before converting to +41 format.
  cleaned = "0" + cleaned;
  return "+41" + cleaned.slice(1);
};

const splitPhoneValues = (phone = "") => {
  if (!phone || typeof phone !== "string") return [];
  return phone
    .split("/")
    .map(part => part.trim())
    .filter(Boolean);
};

export const normalizePhonesForDial = (phone = "") => {
  return splitPhoneValues(phone).map(value => normalizePhoneForDialSingle(value));
};

export const normalizePhoneForDial = (phone = "") => {
  const phones = normalizePhonesForDial(phone);
  return phones[0] || "";
};

/**
 * Returns a human-readable display version of a phone number.
 * Preserves the stored value as-is for display (no forced +41 prefix).
 */
const formatPhoneDisplaySingle = (phone = "") => {
  const cleaned = phone.replace(/[\s.\-\(\)]/g, "");
  let swissLocal = "";

  // +41XXXXXXXXX -> 0XXXXXXXXX
  if (cleaned.startsWith("+41")) {
    swissLocal = "0" + cleaned.slice(3);
  }
  // 0041XXXXXXXXX -> 0XXXXXXXXX
  else if (cleaned.startsWith("0041")) {
    swissLocal = "0" + cleaned.slice(4);
  }
  // 41XXXXXXXXX -> 0XXXXXXXXX
  else if (cleaned.startsWith("41")) {
    swissLocal = "0" + cleaned.slice(2);
  }
  // Already local Swiss format.
  else if (cleaned.startsWith("0")) {
    swissLocal = cleaned;
  }
  // Legacy local Swiss format without leading zero.
  else {
    swissLocal = "0" + cleaned;
  }

  // Swiss spacing format: 0xx xxx xx xx
  if (/^0\d{9}$/.test(swissLocal)) {
    return swissLocal.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, "$1 $2 $3 $4");
  }

  return swissLocal;
};

export const formatPhoneDisplay = (phone = "") => {
  if (!phone || typeof phone !== "string") return "";

  const formatted = splitPhoneValues(phone).map(value =>
    formatPhoneDisplaySingle(value),
  );

  return formatted.join(" / ");
};
